import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { IStore } from '../interfaces/StoreTypes';
import Fuse from 'fuse.js';
import h from '../helpers/common';
import { checkShowLockIcon } from '../helpers/vod';
import f2 from '../helpers/formatter2';
import usePPVManagement from './usePPVManagement';
import { useConfigurationSections } from './useConfiguration';

type TFuseOptions = {
    location?: number;
    threshold?: number;
    distance?: number;
    ignoreLocation?: boolean;
    isCaseSensitive?: boolean;
    minMatchCharLength?: number;
    shouldSort?: boolean;
    findAllMatches?: boolean;
    ignoreFieldNorm?: boolean;
    fieldNormWeight?: number;
};

const useChannelSearch = () => {
    const [query, setQuery] = useState<string>();
    const [count, setCount] = useState<number>(0);

    const rawChannels = useSelector<IStore>((state) => state.liveTv?.userChannels?.channels || []);
    // Using React Query instead of Redux for configuration
    const { fuseJsOptions, channelImage } = useConfigurationSections();
    const fuseOptions: TFuseOptions = fuseJsOptions || {};
    const channelImageId = channelImage?.id || ('colour_264x26' as string);

    // Use centralized PPV management for filtering
    const { filterChannelsForPPVAccess } = usePPVManagement();

    // Filter channels to hide PPV channels without available events
    const channels = useMemo(() => {
        return filterChannelsForPPVAccess(rawChannels as any[]);
    }, [rawChannels, filterChannelsForPPVAccess]);

    useEffect(() => {
        setCount(Array.isArray(channels) ? channels.length : 0);
    }, [channels]);

    const find = (query: string) => {
        setQuery(query);
        const fuse = new Fuse([...(channels as any)], {
            keys: ['channelNumber', 'channelTitle', 'channelTitleAr', 'category'],
            location: fuseOptions?.location || 0,
            threshold: fuseOptions?.threshold || 0.6,
            distance: fuseOptions?.distance || 100,
            ignoreLocation: fuseOptions?.ignoreLocation || false,
            isCaseSensitive: fuseOptions?.isCaseSensitive || false,
            minMatchCharLength: fuseOptions?.minMatchCharLength || 1,
            shouldSort: fuseOptions?.shouldSort || true,
            findAllMatches: fuseOptions?.findAllMatches || false,
            ignoreFieldNorm: fuseOptions?.ignoreFieldNorm || false,
            fieldNormWeight: fuseOptions?.fieldNormWeight || 1.0,
        });
        const results = fuse.search(query);
        setCount(results.length);
        return results.map((result, idx) => {
            const channelImageUrl =
                result?.item?.channelImages?.find((img) => img.id === channelImageId)?.url ||
                (result?.item?.channel?.channelImages[0] && result?.item?.channelImages[0].url) ||
                '';
            const subscriptionGuids = f2.pluck(result?.item.subscriptions, 'subscriptionGUID');

            // Don't pre-calculate lock status here - let the consuming component handle it
            // This avoids race conditions with PPV config loading
            const isLocked = null; // Will be calculated in ChannelsCarousel with proper PPV detection

            return {
                // Search display properties
                id: result?.item?.channelID,
                uuid: result?.item?.channelID,
                logo: channelImageId.toLowerCase().includes('svg')
                    ? channelImageUrl
                    : h.getImagizerUrl(channelImageUrl, 176),
                title: h.getLang() === 'en' ? result?.item?.channelTitle : result?.item?.channelTitleAr,
                locked: isLocked,
                type: 'channel',
                index: idx,

                // Legacy properties for compatibility
                tuningURLs: result?.item?.tuningURLs,
                channelNumber: result?.item?.channelNumber,
                channelImages: result?.item?.channelImages,
                subscriptions: result?.item?.subscriptions,

                // Include the full original channel object for PPV enhancement
                ...result?.item, // Spread all original properties

                // Ensure search-specific properties override if needed
                channelID: result?.item?.channelID, // Make sure channelID is available
            };
        });
    };

    return { find, query, count };
};

export default useChannelSearch;
